import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type AnomalyType =
  | 'sudden_capacity_drop'
  | 'possible_module_loss'
  | 'config_mismatch_suspected'
  | 'insufficient_cycling'
  | 'telemetry_quality_blocked';

export type AnomalySeverity = 'info' | 'watch' | 'warning' | 'critical';
export type AnomalyStatus = 'dormant' | 'reviewed' | 'dismissed' | 'promoted';

export interface BatteryAnomalyRow {
  id: string;
  property_id: string;
  property_name: string;
  plant_id: string;
  anomaly_type: AnomalyType;
  severity: AnomalySeverity;
  confidence: string;
  status: AnomalyStatus;
  expected_capacity_kwh: number | null;
  observed_capacity_kwh: number | null;
  delta_pct: number | null;
  reason_codes: string[];
  blocked_reason: string | null;
  detected_at: string;
  created_at: string;
  baseline_status: string | null;
  baseline_confidence: string | null;
  nameplate_kwh: number | null;
}

async function fetchBatteryAnomalies(): Promise<BatteryAnomalyRow[]> {
  // Join anomalies → properties for name, and baselines for baseline context
  const { data, error } = await supabase
    .from('battery_capacity_anomalies')
    .select(`
      id,
      property_id,
      plant_id,
      anomaly_type,
      severity,
      confidence,
      status,
      expected_capacity_kwh,
      observed_capacity_kwh,
      delta_pct,
      reason_codes,
      blocked_reason,
      detected_at,
      created_at,
      metadata
    `)
    .order('detected_at', { ascending: false });

  if (error) throw error;

  // Fetch property names in a single query
  const propertyIds = [...new Set((data ?? []).map((r: Record<string, unknown>) => r.property_id as string))];
  const { data: props } = await supabase
    .from('properties')
    .select('id, name')
    .in('id', propertyIds);
  const propMap = new Map((props ?? []).map((p: { id: string; name: string }) => [p.id, p.name]));

  // Fetch baseline context
  const plantIds = [...new Set((data ?? []).map((r: Record<string, unknown>) => r.plant_id as string))];
  const { data: baselines } = await supabase
    .from('battery_capacity_baselines')
    .select('plant_id, status, confidence, nameplate_kwh')
    .in('plant_id', plantIds);
  const baselineMap = new Map(
    (baselines ?? []).map((b: { plant_id: string; status: string; confidence: string; nameplate_kwh: number | null }) =>
      [b.plant_id, b]
    )
  );

  return (data ?? []).map((r: Record<string, unknown>) => {
    const bl = baselineMap.get(r.plant_id as string);
    return {
      id: r.id as string,
      property_id: r.property_id as string,
      property_name: propMap.get(r.property_id as string) ?? 'Unknown',
      plant_id: r.plant_id as string,
      anomaly_type: r.anomaly_type as AnomalyType,
      severity: r.severity as AnomalySeverity,
      confidence: r.confidence as string,
      status: r.status as AnomalyStatus,
      expected_capacity_kwh: r.expected_capacity_kwh as number | null,
      observed_capacity_kwh: r.observed_capacity_kwh as number | null,
      delta_pct: r.delta_pct as number | null,
      reason_codes: (r.reason_codes ?? []) as string[],
      blocked_reason: r.blocked_reason as string | null,
      detected_at: r.detected_at as string,
      created_at: r.created_at as string,
      baseline_status: bl?.status ?? null,
      baseline_confidence: bl?.confidence ?? null,
      nameplate_kwh: bl?.nameplate_kwh ?? null,
    };
  });
}

export function useAdminBatteryAnomalies(enabled: boolean) {
  return useQuery({
    queryKey: ['admin-battery-anomalies'],
    queryFn: fetchBatteryAnomalies,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Status update mutation ──────────────────────────────────────────

export type AllowedStatusUpdate = 'reviewed' | 'dismissed';

interface UpdateAnomalyStatusParams {
  anomalyId: string;
  status: AllowedStatusUpdate;
  note?: string;
}

async function updateAnomalyStatus({ anomalyId, status, note }: UpdateAnomalyStatusParams): Promise<string> {
  const { data, error } = await supabase.rpc('update_battery_anomaly_status', {
    p_anomaly_id: anomalyId,
    p_status: status,
    p_note: note ?? null,
  });
  if (error) throw error;
  return data as string;
}

export function useUpdateAnomalyStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAnomalyStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-battery-anomalies'] });
    },
  });
}
